// Client-side media analysis for the timeline: waveform peaks, beat/onset detection,
// and video thumbnail filmstrips. All cached per-URL. Best-effort and non-blocking —
// callers render a placeholder until the promise resolves.

const waveCache = new Map();
const thumbCache = new Map();

function getAudioCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  return AC ? new AC() : null;
}

// Decode the media's audio and return { peaks:[0..1], duration, beats:[t] }.
export async function getWaveform(url, buckets = 1200) {
  if (!url) return { peaks: [], duration: 0, beats: [] };
  if (waveCache.has(url)) return waveCache.get(url);
  const p = (async () => {
    try {
      const ctx = getAudioCtx();
      if (!ctx) return { peaks: [], duration: 0, beats: [] };
      const res = await fetch(url);
      const buf = await res.arrayBuffer();
      const audio = await ctx.decodeAudioData(buf);
      const ch = audio.getChannelData(0);
      const n = ch.length;
      const step = Math.max(1, Math.floor(n / buckets));
      const peaks = [];
      let gmax = 0.0001;
      for (let i = 0; i < n; i += step) {
        let max = 0;
        for (let j = i; j < i + step && j < n; j++) { const a = Math.abs(ch[j]); if (a > max) max = a; }
        peaks.push(max);
        if (max > gmax) gmax = max;
      }
      const norm = peaks.map((v) => v / gmax);
      const beats = detectBeats(ch, audio.sampleRate, audio.duration);
      try { ctx.close(); } catch { /* noop */ }
      return { peaks: norm, duration: audio.duration, beats };
    } catch {
      return { peaks: [], duration: 0, beats: [] };
    }
  })();
  waveCache.set(url, p);
  return p;
}

// Simple energy-based onset detection → beat/accent times (seconds).
function detectBeats(ch, sampleRate, duration) {
  try {
    const win = 1024;
    const hop = 512;
    const energies = [];
    for (let i = 0; i + win < ch.length; i += hop) {
      let e = 0;
      for (let j = i; j < i + win; j++) e += ch[j] * ch[j];
      energies.push(e / win);
    }
    if (!energies.length) return [];
    // Moving average threshold; onset when energy jumps above local average.
    const beats = [];
    const avgWin = 24;
    let last = -1;
    const minGap = Math.round((0.25 * sampleRate) / hop); // ≥250ms apart
    for (let i = 1; i < energies.length; i++) {
      const s = Math.max(0, i - avgWin);
      let mean = 0;
      for (let k = s; k < i; k++) mean += energies[k];
      mean /= (i - s) || 1;
      if (energies[i] > mean * 1.6 && energies[i] > 0.0004 && (last < 0 || i - last >= minGap)) {
        beats.push((i * hop) / sampleRate);
        last = i;
      }
    }
    return beats.filter((t) => t > 0.05 && t < duration - 0.02).slice(0, 400);
  } catch {
    return [];
  }
}

function seekTo(video, t) {
  return new Promise((resolve) => {
    let done = false;
    const onSeeked = () => { if (done) return; done = true; video.removeEventListener("seeked", onSeeked); resolve(); };
    video.addEventListener("seeked", onSeeked);
    try { video.currentTime = Math.min(Math.max(0, t), (video.duration || 0.1) - 0.02); } catch { resolve(); }
    setTimeout(onSeeked, 1500); // safety
  });
}

// Extract a filmstrip of thumbnails (data URLs) evenly across the video.
export async function getVideoThumbnails(url, count = 12) {
  if (!url) return { thumbs: [], aspect: 16 / 9 };
  if (thumbCache.has(url)) return thumbCache.get(url);
  const p = new Promise((resolve) => {
    const v = document.createElement("video");
    v.crossOrigin = "anonymous";
    v.src = url; v.muted = true; v.preload = "auto"; v.playsInline = true;
    let settled = false;
    const fail = () => { if (!settled) { settled = true; resolve({ thumbs: [], aspect: 16 / 9 }); } };
    v.addEventListener("error", fail);
    v.addEventListener("loadedmetadata", async () => {
      try {
        const dur = v.duration || 0;
        const aspect = v.videoWidth && v.videoHeight ? v.videoWidth / v.videoHeight : 16 / 9;
        const cw = 96, chh = Math.max(40, Math.round(cw / aspect));
        const cv = document.createElement("canvas"); cv.width = cw; cv.height = chh;
        const ctx = cv.getContext("2d");
        const thumbs = [];
        for (let i = 0; i < count; i++) {
          const t = ((i + 0.5) / count) * dur;
          await seekTo(v, t);
          try { ctx.drawImage(v, 0, 0, cw, chh); thumbs.push({ t, url: cv.toDataURL("image/jpeg", 0.5) }); }
          catch { break; }
        }
        settled = true;
        resolve({ thumbs, aspect });
      } catch { fail(); }
    }, { once: true });
  });
  thumbCache.set(url, p);
  return p;
}
